import React, { useState, useMemo } from 'react';
import { Box, Typography, IconButton, Tooltip, Chip, Button } from 'components/mui';
import {
  Paper,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Collapse,
  Select,
  FormControl,
  InputLabel,
  OutlinedInput,
  MenuItem,
  Checkbox,
  ListItemText,
  Skeleton,
  Fade,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FolderOffIcon from '@mui/icons-material/FolderOff';
import type { MyCaseSummary } from 'containers/pages/cantrackDashboard/types';

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const STATUS_COLOR_MAP: Record<string, 'success' | 'warning' | 'error' | 'info' | 'primary'> = {
  Open: 'info',
  'In Progress': 'warning',
  Closed: 'success',
  Cancelled: 'error',
  'On Hold': 'warning',
};

const STATUS_OPTIONS = ['Open', 'In Progress', 'Closed', 'Cancelled', 'On Hold'] as const;

const STATUS_ID_TO_TITLE: Record<number, string> = {
  1: 'Open',
  2: 'In Progress',
  3: 'Closed',
  4: 'Cancelled',
  5: 'On Hold',
};

const DISPLAY_INCREMENT = 10;

function getStatusLabel(item: MyCaseSummary): string {
  return item.StatusTitle || STATUS_ID_TO_TITLE[item.StatusId] || 'N/A';
}

function getStatusColor(status: string): 'success' | 'warning' | 'error' | 'info' | 'primary' {
  return STATUS_COLOR_MAP[status] || 'primary';
}

function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return '--';
  const dotNetMatch = /\/Date\((-?\d+)\)\//.exec(dateString);
  const d = dotNetMatch
    ? new Date(parseInt(dotNetMatch[1], 10))
    : new Date(dateString);
  if (isNaN(d.getTime())) return '--';
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

interface MyCasesSectionProps {
  cases: MyCaseSummary[];
  loading: boolean;
  onCaseClick: (caseId: number) => void;
}

/* ------------------------------------------------------------------ */
/*  MyCasesSection                                                     */
/* ------------------------------------------------------------------ */

function MyCasesSection({ cases, loading, onCaseClick }: MyCasesSectionProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchInput, setSearchInput] = useState('');
  const [statusFilterValues, setStatusFilterValues] = useState<string[]>([]);
  const [expandedRowId, setExpandedRowId] = useState<number | null>(null);
  const [displayCount, setDisplayCount] = useState(DISPLAY_INCREMENT);

  const filteredCases = useMemo(() => {
    let result = cases;

    if (searchInput.trim()) {
      const term = searchInput.trim().toLowerCase();
      result = result.filter(
        (c) =>
          c.Title.toLowerCase().includes(term) ||
          c.CustomerName.toLowerCase().includes(term) ||
          String(c.Id).includes(term),
      );
    }

    if (statusFilterValues.length > 0) {
      result = result.filter((c) => statusFilterValues.includes(c.StatusTitle));
    }

    return result;
  }, [cases, searchInput, statusFilterValues]);

  const visibleCases = filteredCases.slice(0, displayCount);
  const hasMore = displayCount < filteredCases.length;

  const handleToggleExpand = (event: React.MouseEvent, caseId: number) => {
    event.stopPropagation();
    setExpandedRowId((prev) => (prev === caseId ? null : caseId));
  };

  const handleShowMore = () => {
    setDisplayCount((prev) => prev + DISPLAY_INCREMENT);
  };

  if (loading) {
    return <LoadingSkeleton />;
  }

  return (
    <Box>
      {/* Title + Control bar in Paper */}
      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          borderBottom: 0,
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
        }}
      >
        <Typography variant="h6" fontWeight={600} sx={{ mb: 1.5 }}>
          My Cases
        </Typography>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <TextField
            size="small"
            placeholder="Search cases..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
              endAdornment: searchInput ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchInput('')} aria-label="Clear search">
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{ minWidth: 220, flex: { xs: '1 1 100%', sm: '0 1 280px' } }}
          />

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="my-cases-status-filter-label">Status</InputLabel>
            <Select
              labelId="my-cases-status-filter-label"
              multiple
              value={statusFilterValues}
              onChange={(e) => setStatusFilterValues(e.target.value as string[])}
              input={<OutlinedInput label="Status" />}
              renderValue={(selected) => (selected as string[]).join(', ')}
            >
              {STATUS_OPTIONS.map((status) => (
                <MenuItem key={status} value={status}>
                  <Checkbox size="small" checked={statusFilterValues.includes(status)} />
                  <ListItemText primary={status} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ flex: 1 }} />

          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            Showing {visibleCases.length} of {filteredCases.length} cases
          </Typography>
        </Box>
      </Paper>

      {/* Content */}
      {filteredCases.length === 0 ? (
        <EmptyState />
      ) : isMobile ? (
        <Fade in>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {visibleCases.map((caseItem) => (
              <MobileCaseCard
                key={caseItem.Id}
                caseItem={caseItem}
                onClick={() => onCaseClick(caseItem.Id)}
              />
            ))}
            {hasMore && (
              <Button size="small" onClick={handleShowMore} sx={{ alignSelf: 'center' }}>
                Show More
              </Button>
            )}
          </Box>
        </Fade>
      ) : (
        <Fade in>
          <Paper variant="outlined" sx={{ overflow: 'visible', borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 600, bgcolor: 'grey.50' } }}>
                  <TableCell sx={{ width: 40 }} />
                  <TableCell>Case Title</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Last Updated</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleCases.map((caseItem) => (
                  <CaseRow
                    key={caseItem.Id}
                    caseItem={caseItem}
                    expanded={expandedRowId === caseItem.Id}
                    onToggleExpand={(e) => handleToggleExpand(e, caseItem.Id)}
                    onClick={() => onCaseClick(caseItem.Id)}
                  />
                ))}
              </TableBody>
            </Table>
            {hasMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
                <Button size="small" onClick={handleShowMore}>
                  Show More
                </Button>
              </Box>
            )}
          </Paper>
        </Fade>
      )}
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/*  MobileCaseCard                                                     */
/* ------------------------------------------------------------------ */

function MobileCaseCard({
  caseItem,
  onClick,
}: {
  caseItem: MyCaseSummary;
  onClick: () => void;
}) {
  const status = getStatusLabel(caseItem);
  return (
    <Paper
      variant="outlined"
      onClick={onClick}
      sx={{ p: 2, cursor: 'pointer', '&:hover': { boxShadow: 3 } }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body2" fontWeight={600} noWrap>
            {caseItem.Title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            #{caseItem.Id}
          </Typography>
        </Box>
        <Chip
          label={status}
          size="small"
          color={getStatusColor(status)}
          sx={{ fontWeight: 600, fontSize: '0.75rem', ml: 1 }}
        />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="caption" color="text.secondary" noWrap>
          {caseItem.CustomerName || '--'}
        </Typography>
        <Typography variant="caption" color="text.disabled">
          {formatDate(caseItem.UpdatedOn)}
        </Typography>
      </Box>
    </Paper>
  );
}

/* ------------------------------------------------------------------ */
/*  CaseRow                                                            */
/* ------------------------------------------------------------------ */

interface CaseRowProps {
  caseItem: MyCaseSummary;
  expanded: boolean;
  onToggleExpand: (e: React.MouseEvent) => void;
  onClick: () => void;
}

function CaseRow({ caseItem, expanded, onToggleExpand, onClick }: CaseRowProps) {
  return (
    <>
      <TableRow
        hover
        onClick={onClick}
        sx={{ cursor: 'pointer', '& > td': { borderBottom: expanded ? 0 : undefined } }}
      >
        <TableCell padding="none" onClick={(e) => e.stopPropagation()}>
          <IconButton size="small" onClick={onToggleExpand} aria-label={expanded ? 'Collapse row' : 'Expand row'}>
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 300 }}>
            {caseItem.Title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            #{caseItem.Id}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" noWrap sx={{ maxWidth: 160 }}>
            {caseItem.CustomerName || '--'}
          </Typography>
        </TableCell>
        <TableCell>
          <Chip
            label={getStatusLabel(caseItem)}
            size="small"
            color={getStatusColor(getStatusLabel(caseItem))}
            sx={{ fontWeight: 600, fontSize: '0.75rem' }}
          />
        </TableCell>
        <TableCell>
          <Typography variant="body2" color="text.secondary" noWrap>
            {formatDate(caseItem.UpdatedOn)}
          </Typography>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={5} sx={{ py: 0, px: 0 }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ p: 2, pl: 7, bgcolor: 'grey.50' }}>
              <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', mb: 1 }}>
                <DetailField label="Project" value={caseItem.ProjectTitle} />
                <DetailField label="Group" value={caseItem.GroupTitle} />
                <DetailField label="Time Spent" value={caseItem.TimeSpent || '0h'} />
                <DetailField label="Activities" value={String(caseItem.NumberOfActivities ?? 0)} />
              </Box>
              {caseItem.Description && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1, '& p': { m: 0 } }}
                  dangerouslySetInnerHTML={{
                    __html: caseItem.Description.length > 300
                      ? `${caseItem.Description.substring(0, 300)}...`
                      : caseItem.Description,
                  }}
                />
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  Small sub-components                                               */
/* ------------------------------------------------------------------ */

function DetailField({ label, value }: { label: string; value: string | undefined | null }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={500}>
        {value || '--'}
      </Typography>
    </Box>
  );
}

function EmptyState() {
  return (
    <Paper
      variant="outlined"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 8,
        px: 3,
      }}
    >
      <FolderOffIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
      <Typography variant="h6" color="text.secondary" gutterBottom>
        No open cases assigned to you
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Cases assigned to you will appear here.
      </Typography>
    </Paper>
  );
}

function LoadingSkeleton() {
  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', gap: 2, p: 1.5, bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider' }}>
        <Skeleton variant="text" width={120} />
        <Skeleton variant="text" width={100} />
        <Skeleton variant="text" width={80} />
        <Skeleton variant="text" width={90} />
      </Box>
      {Array.from({ length: 5 }).map((_, i) => (
        <Box
          key={i}
          sx={{
            display: 'flex',
            gap: 2,
            p: 1.5,
            alignItems: 'center',
            borderBottom: 1,
            borderColor: 'divider',
            '&:last-child': { borderBottom: 0 },
          }}
        >
          <Skeleton variant="text" width={180 + Math.random() * 80} />
          <Skeleton variant="text" width={100 + Math.random() * 40} />
          <Skeleton variant="rounded" width={70} height={24} />
          <Skeleton variant="text" width={80} />
        </Box>
      ))}
    </Paper>
  );
}

export default MyCasesSection;
